import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './Player.css';
import {useEffect, useRef, useState} from 'react';
import videojsChromecast from '@silvermine/videojs-chromecast';
import * as Sentry from '@sentry/react';

// @silvermine/videojs-airplay is incompatible with video.js v8 (uses removed videojs.extend).
// AirPlay works natively in Safari on iOS/macOS via the HTML5 video element without a plugin.

const Player = ({options, onReady, style}) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    videojsChromecast(videojs, {preloadWebComponents: true});

    useEffect(() => {
        if (videoRef.current) {
            const video = videoRef.current;

            playerRef.current = videojs(video, {
                autoplay: options.autoplay,
                controls: options.controls,
                preload: 'auto',
                userActions: {
                    hotkeys: true,
                },
                controlBar: {
                    pictureInPictureToggle: false,
                },
                poster: options.poster,
                sources: options.sources,
                techOrder: ['chromecast', 'html5'],
            });

            playerRef.current.chromecast();

            playerRef.current.on('error', () => {
                const err = playerRef.current.error();
                if (err) {
                    Sentry.captureException(new Error(`Video.js error: ${err.message}`), {
                        tags: { component: 'Player', videoErrorCode: err.code },
                        extra: {
                            status: err.status,
                            src: playerRef.current.currentSrc(),
                        },
                    });
                }
            });

            playerRef.current.on('play', () => {
                playerRef.current.bigPlayButton.hide();
            });

            playerRef.current.on('pause', () => {
                playerRef.current.bigPlayButton.show();
            });

            onReady(playerRef);
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
            }
        };
    }, [videoRef]);

    useEffect(() => {
        playerRef.current.poster(options.poster);
        playerRef.current.src(options.sources);
        if (options.autoplay) playerRef.current.play();
    }, [options]);

    // Dispose the Video.js player when the functional component unmounts
    useEffect(() => {
        const player = playerRef.current;

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);

    return (
        <div className='aspect-video w-full rounded-lg overflow-hidden '>
            <video ref={videoRef} className={'video video-js w-full h-auto aspect-video'} style={style}/>
        </div>
    );
};

export default Player;
